
import { Link } from 'react-router-dom';
import {
  Monitor, Shield, Cloud, Cable,
  Lock, ArrowRight, Phone, CheckCircle, Building,
  Server, Mail, Database, Users, Wifi, Camera, Star,
  FileCheck, ShieldCheck, BadgeCheck
} from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { ImageSitemapLink } from '../components/seo/ImageSitemap';
import { OwnerProfile } from '../components/OwnerProfile';
import { LocalBusinessSchema } from '../components/seo/LocalBusinessSchema';
import { GlassCard } from '../components/ui/GlassCard';
import { BentoGrid, BentoItem } from '../components/ui/BentoGrid';
import { GoogleReviews } from '../components/GoogleReviews';
import { ValuePillars } from '../components/ValuePillars';
import { HomepageFAQ } from '../components/HomepageFAQ';
import { WhatHappensNext } from '../components/WhatHappensNext';
import { TrackedPhoneLink } from '../components/ui/TrackedPhoneLink';
import { InlineQuoteForm } from '../components/InlineQuoteForm';
import { CountUp } from '../components/ui/CountUp';
import { DigitalPulse } from '../components/ui/DigitalPulse';
import { BlueprintAnimation } from '../components/ui/BlueprintAnimation';

export function HomePage() {
  return (
    <div className="min-h-screen bg-navy-900 text-gray-100 font-body selection:bg-cobalt-500/30">
      <SEO
        title="Network Cabling & IT Support Boise & Meridian 2026"
        description="200+ Treasure Valley businesses trust XXL IT. Cabling, UniFi, cameras. 25-yr warranty. Free site survey → (208) 328-4500"
        keywords={[
          'low voltage cabling Boise',
          'data cabling Meridian',
          'UniFi installation Idaho',
          'security camera installation Boise',
          'network cabling Treasure Valley',
          'business network installation',
          'IT support Boise',
          'IT support Meridian',
          'managed IT services Treasure Valley',
          'cybersecurity services Boise'
        ]}
      />
      <LocalBusinessSchema />
      <ImageSitemapLink />

      <main className="pt-0">
        {/* HERO SECTION - Glassmorphism */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-24 pb-12 sm:pt-32 sm:pb-20">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <picture>
              <source media="(max-width: 640px)" srcSet="/images/hero-it-infrastructure-mobile.webp" type="image/webp" />
              <source media="(max-width: 1024px)" srcSet="/images/hero-it-infrastructure-tablet.webp" type="image/webp" />
              <img
                src="/images/hero-it-infrastructure-optimized.jpg"
                alt="Modern IT infrastructure"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </picture>
            {/* Gradient Overlays */}
            <div className="absolute inset-0 bg-navy-900/90 mix-blend-multiply" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 via-transparent to-navy-900/80" />
          </div>

          {/* Digital Pulse Overlay */}
          <DigitalPulse />

          {/* Content */}
          <div className="relative z-10 container mx-auto px-4 text-center">

            {/* Warranty Badge - Glass */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-md mb-4 sm:mb-8">
              <CheckCircle className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-semibold text-yellow-200 tracking-wider uppercase">25-Year Warranty on All Cabling</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-sans font-bold text-white mb-4 sm:mb-6 tracking-tight leading-tight drop-shadow-2xl">
              Your Business Runs Better <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cobalt-400 to-cyan-300">
                When the Network Just Works
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-xl md:text-2xl text-gray-300 mb-6 sm:mb-10 max-w-3xl mx-auto font-light leading-relaxed">
              Treasure Valley's trusted installer for <span className="text-safety-orange font-medium">low voltage cabling</span>, <span className="text-cobalt-400 font-medium">UniFi systems</span>, and <span className="text-white font-medium">security cameras</span>.
              <br className="hidden md:block" /> Serving Boise, Meridian, Nampa, and beyond.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="#get-quote"
                className="group relative px-8 py-4 bg-cobalt-600 rounded-full text-white font-bold text-lg shadow-[0_0_20px_rgba(59,98,240,0.5)] hover:bg-cobalt-500 hover:shadow-[0_0_30px_rgba(59,98,240,0.8)] transition-all duration-300 transform hover:-translate-y-1"
              >
                Get a Free Quote
                <ArrowRight className="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>

              <TrackedPhoneLink
                location="homepage_hero"
                className="flex items-center gap-2 px-8 py-4 rounded-full border border-white/10 bg-white/5 backdrop-blur-md hover:bg-white/10 transition-all duration-300 text-white font-medium text-lg"
              >
                <Phone className="w-5 h-5 text-gray-300" />
                (208) 328-4500
              </TrackedPhoneLink>
            </div>

            {/* Quick reassurance line under CTAs */}
            <p className="mt-4 text-sm text-gray-400 flex flex-wrap justify-center gap-x-2">
              <span className="whitespace-nowrap">Free site survey</span>
              <span aria-hidden="true">&middot;</span>
              <span className="whitespace-nowrap">1-business-hour callback</span>
              <span aria-hidden="true">&middot;</span>
              <span className="whitespace-nowrap">No obligation</span>
            </p>

            {/* Stats / Trust Signals */}
            <div className="mt-10 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto border-t border-white/10 pt-8">
              {[
                { label: "Businesses Served", value: 200, suffix: "+" },
                { label: "Installations", value: 500, suffix: "+" },
                { label: "Response Time", value: 15, suffix: " min" },
                { label: "Year Warranty", value: 25, suffix: " yr" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold text-white mb-1">
                    <CountUp end={stat.value} suffix={stat.suffix} duration={2} />
                  </div>
                  <div className="text-sm text-gray-300 uppercase tracking-widest font-sans">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="mt-8 flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              {[
                { icon: BadgeCheck, label: 'BICSI Certified', color: 'text-emerald-400' },
                { icon: Shield, label: 'BBB A+ Rated', color: 'text-blue-400' },
                { icon: FileCheck, label: 'Licensed #3171776', color: 'text-green-400' },
                { icon: ShieldCheck, label: 'Fully Insured', color: 'text-blue-400' },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
                  <badge.icon className={`w-4 h-4 ${badge.color}`} />
                  <span className="text-xs font-semibold text-gray-300">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ABOVE-FOLD LEAD CAPTURE - Anchor target for hero CTA */}
        <section id="get-quote" data-hide-sticky-cta className="py-16 bg-navy-900 scroll-mt-20">
          <div className="container mx-auto px-4">
            {/* Service bullets — answers "what do you do?" before the form */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto mb-8 items-stretch">
              {[
                { icon: Cable, label: 'Network Cabling', color: 'text-safety-orange' },
                { icon: Wifi, label: 'Business WiFi & UniFi', color: 'text-cobalt-400' },
                { icon: Camera, label: 'Security Cameras', color: 'text-emerald-400' },
                { icon: Monitor, label: 'IT Support', color: 'text-purple-400' },
              ].map((s, i) => (
                <div key={i} className="h-full flex items-center gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/5">
                  <s.icon className={`w-5 h-5 ${s.color} flex-shrink-0`} />
                  <span className="text-sm md:text-base font-semibold text-gray-100 leading-tight">{s.label}</span>
                </div>
              ))}
            </div>

            <InlineQuoteForm
              source="homepage_hero"
              headline="IT Support and Network Cabling in Boise and Meridian"
              subheadline="Request Your Free Site Survey"
              variant="dark"
            />

            {/* Trust strip — proof under the form */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span><strong className="text-white">20+ Years</strong> IT Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span><strong className="text-white">5-Star</strong> Google Rated</span>
              </div>
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-cobalt-400" />
                <span>Serving <strong className="text-white">Boise, Meridian & Nampa</strong></span>
              </div>
            </div>
          </div>
        </section>

        {/* VALUE PILLARS */}
        <ValuePillars />

        {/* BLUEPRINT TO REALITY */}
        <section className="py-24 bg-navy-950 relative border-t border-white/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                From <span className="text-cobalt-400">Blueprint</span> to Reality
              </h2>
              <p className="text-xl text-gray-300">
                Whether it's a warehouse, new office build, or retail rollout, we engineer the physical layer so the logical layer runs flawlessly.
              </p>
            </div>
            <BlueprintAnimation />
          </div>
        </section>

        {/* BENTO GRID - SERVICES (70/30 Split) */}
        <section className="py-24 bg-navy-950 relative">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cobalt-900 to-transparent" />

          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">IT Support & Network Cabling Services in Boise, Meridian, Nampa</h2>
              <p className="text-gray-300 max-w-2xl mx-auto text-lg">
                Specializing in physical infrastructure (Cabling, Cameras, Access Control) and managed IT support.
              </p>
            </div>

            <BentoGrid>
              {/* PRIMARY FOCUS (70%) - Cabling & Infrastructure */}

              {/* Featured Item: Structured Cabling - Large Tile */}
              <BentoItem span={2} className="row-span-2">
                <GlassCard className="h-full group p-0" hoverEffect={true}>
                  <Link to="/services/data-cabling" className="block h-full relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-safety-orange/20 to-transparent z-0 opacity-20 group-hover:opacity-30 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-8 z-20 bg-gradient-to-t from-navy-900 to-transparent">
                      <div className="mb-4 inline-flex p-3 rounded-xl bg-safety-orange/10 border border-safety-orange/20 text-safety-orange">
                        <Cable className="w-8 h-8" />
                      </div>
                      <div className="absolute top-4 right-4 bg-navy-900/80 backdrop-blur-sm border border-safety-orange/30 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transform translate-y-[-10px] group-hover:translate-y-0 transition-all duration-300 shadow-[0_0_15px_rgba(255,102,0,0.2)]">
                        <span className="text-xs font-bold text-safety-orange tracking-wider uppercase">10Gbps Ready &middot; Certified Testing</span>
                      </div>
                      <h3 className="text-3xl font-bold text-white mb-2">Structured Data Cabling</h3>
                      <p className="text-gray-300 mb-4 text-lg">Cat6/Cat6a certified installation with a 25-year warranty. The backbone of your business.</p>
                      <span className="text-safety-orange font-semibold flex items-center group-hover:translate-x-2 transition-transform">
                        Explore Cabling <ArrowRight className="w-4 h-4 ml-2" />
                      </span>
                    </div>
                    <img src="/images/hero-it-infrastructure-optimized.jpg" alt="Cabling" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" />
                  </Link>
                </GlassCard>
              </BentoItem>

              {/* Security Cameras */}
              <BentoItem span={1} className="row-span-1">
                <GlassCard className="h-full p-8 flex flex-col justify-between group">
                  <Link to="/services/security-cameras" className="h-full flex flex-col">
                    <div className="bg-cobalt-500/20 p-3 rounded-xl w-fit mb-4 text-cobalt-400">
                      <Camera className="w-6 h-6" />
                    </div>
                    <div className="absolute top-4 right-4 bg-navy-900/80 backdrop-blur-sm border border-cobalt-500/30 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transform translate-y-[-10px] group-hover:translate-y-0 transition-all duration-300 shadow-[0_0_15px_rgba(59,98,240,0.2)]">
                      <span className="text-xs font-bold text-cobalt-400 tracking-wider uppercase">4K Resolution &middot; Local NVR</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Security Cameras</h3>
                      <p className="text-gray-300 text-sm">UniFi Protect 4K surveillance systems with local NVR.</p>
                    </div>
                    <div className="mt-auto pt-4 flex justify-end">
                      <ArrowRight className="text-cobalt-400 opacity-0 group-hover:opacity-100 transition-opacity transform -translate-x-2 group-hover:translate-x-0" />
                    </div>
                  </Link>
                </GlassCard>
              </BentoItem>

              {/* UniFi Installation */}
              <BentoItem span={1} className="row-span-1">
                <GlassCard className="h-full p-8 flex flex-col justify-between group">
                  <Link to="/services/unifi-installation" className="h-full flex flex-col">
                    <div className="bg-cobalt-500/20 p-3 rounded-xl w-fit mb-4 text-cobalt-400">
                      <Wifi className="w-6 h-6" />
                    </div>
                    <div className="absolute top-4 right-4 bg-navy-900/80 backdrop-blur-sm border border-cobalt-500/30 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transform translate-y-[-10px] group-hover:translate-y-0 transition-all duration-300 shadow-[0_0_15px_rgba(59,98,240,0.2)]">
                      <span className="text-xs font-bold text-cobalt-400 tracking-wider uppercase">Enterprise Class &middot; Zero Fees</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">UniFi Networks</h3>
                      <p className="text-gray-300 text-sm">Enterprise WiFi & Switching with zero license fees.</p>
                    </div>
                    <div className="mt-auto pt-4 flex justify-end">
                      <ArrowRight className="text-cobalt-400 opacity-0 group-hover:opacity-100 transition-opacity transform -translate-x-2 group-hover:translate-x-0" />
                    </div>
                  </Link>
                </GlassCard>
              </BentoItem>

              {/* SECONDARY FOCUS (30%) - Managed IT Services */}

              {/* Managed IT Support */}
              <BentoItem span={3}>
                <GlassCard className="p-8 group relative overflow-hidden">
                  <Link to="/services/it-support" className="flex flex-col md:flex-row items-center gap-8">
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-6 rounded-2xl flex-shrink-0">
                      <Monitor className="w-10 h-10 text-white" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-2xl font-bold text-white mb-2">Managed IT Support</h3>
                      <p className="text-gray-300">
                        We handle the blinky lights so you can handle business. 24/7 monitoring, patch management, and helpdesk.
                      </p>
                    </div>
                    <div className="flex-shrink-0 border border-white/10 rounded-full px-6 py-2 bg-white/5 group-hover:bg-white/10 transition-colors">
                      <span className="text-sm font-semibold text-white">Full Service IT</span>
                    </div>
                  </Link>
                </GlassCard>
              </BentoItem>

              {/* Cybersecurity */}
              <BentoItem span={1}>
                <GlassCard className="h-full p-6 group">
                  <Link to="/services/cybersecurity" className="block h-full">
                    <Shield className="w-8 h-8 text-red-500 mb-4" />
                    <h3 className="text-lg font-bold text-white">Cybersecurity</h3>
                    <p className="text-gray-300 text-sm mt-2">Threat protection & audits.</p>
                  </Link>
                </GlassCard>
              </BentoItem>

              {/* Network */}
              <BentoItem span={1}>
                <GlassCard className="h-full p-6 group">
                  <Link to="/services/network" className="block h-full">
                    <Server className="w-8 h-8 text-green-500 mb-4" />
                    <h3 className="text-lg font-bold text-white">Network Install</h3>
                    <p className="text-gray-300 text-sm mt-2">Router & firewall setup.</p>
                  </Link>
                </GlassCard>
              </BentoItem>

              {/* VoIP */}
              <BentoItem span={1}>
                <GlassCard className="h-full p-6 group">
                  <Link to="/services/voip-phone-systems" className="block h-full">
                    <Phone className="w-8 h-8 text-yellow-500 mb-4" />
                    <h3 className="text-lg font-bold text-white">VoIP Systems</h3>
                    <p className="text-gray-300 text-sm mt-2">Modern business phones.</p>
                  </Link>
                </GlassCard>
              </BentoItem>

            </BentoGrid>
          </div>
        </section>

        {/* OWNER SECTION - "The Human Element" */}
        <section className="py-20 bg-navy-900 border-t border-white/5">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-12 max-w-6xl mx-auto">
              <div className="w-full md:w-1/2 relative flex justify-center">
                <div className="absolute inset-0 bg-gradient-to-r from-cobalt-600 to-indigo-600 blur-3xl opacity-20 rounded-full" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl max-w-sm mx-auto">
                  <img src="/edin-mujcin.jpg" alt="Edin Mujcin" className="w-full h-auto grayscale hover:grayscale-0 transition-all duration-500" />
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Local Expert, <br /><span className="text-cobalt-400">Personal Service.</span></h2>
                <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                  "I've been serving Treasure Valley businesses since 2005. When you call XXL IT, you're not getting a call center halfway around the world. You're getting a local partner who visits your site, knows your name, and understands your business."
                </p>
                <div className="flex items-center gap-4">
                  <div className="text-left">
                    <div className="text-white font-bold text-lg">Edin Mujcin</div>
                    <div className="text-cobalt-400 text-sm uppercase tracking-wide">Owner & IT Director</div>
                  </div>
                  {/* Signature or visual element could go here */}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOMEPAGE FAQ */}
        <HomepageFAQ />

        {/* GOOGLE REVIEWS */}
        <GoogleReviews />

        {/* HOW IT WORKS */}
        <WhatHappensNext variant="dark" />

        {/* INLINE QUOTE FORM */}
        <section data-hide-sticky-cta className="py-16 bg-navy-900">
          <div className="container mx-auto px-4">
            <InlineQuoteForm
              source="homepage"
              headline="IT Support and Network Cabling in Boise and Meridian"
              subheadline="Get Your Free Site Survey"
              variant="dark"
            />
          </div>
        </section>

        {/* LOCAL HERO - Skyline Background */}
        <section className="relative py-24 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0 bg-navy-950">
            {/* Use a local image here if available, falling back to a generic modern city/tech background */}
            <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=80&w=2613&auto=format&fit=crop')] bg-cover bg-center grayscale" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent" />
          </div>

          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Proudly Serving Treasure Valley</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {['Boise', 'Meridian', 'Nampa', 'Eagle', 'Caldwell', 'Kuna'].map((city) => (
                <Link key={city} to={`/location/${city.toLowerCase()}`} className="px-6 py-3 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cobalt-500/50 transition-all text-white font-medium">
                  {city}
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}